import { useState } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Box, Button, Chip, Collapse, Divider, MenuItem, Stack, TextField, Typography
} from '@mui/material';
import type { Folder, SearchParams } from '../api/types';
import { layoutTokens } from '../theme/archiveTheme';

/**
 * Search options that cannot be expressed directly in the query, followed by
 * concise reminders for the syntax that can. Everything here is index-backed;
 * no option silently turns a quick lookup into a corpus scan.
 */

interface AdvancedSearchProps {
  value: SearchParams;
  folders: Folder[];
  onChange: (next: SearchParams) => void;
}

const syntax: Array<{ example: string; means: string }> = [
  { example: '"genaue Phrase"', means: 'genaue Wortfolge' },
  { example: 'Vertrag*', means: 'beginnt mit' },
  { example: '-Anlage', means: 'ausschließen' },
  { example: 'Kündigung OR Aufhebung', means: 'eines von beiden' },
  { example: 'Frist NEAR/5 Kündigung', means: 'maximal 5 Wörter Abstand' }
];

const fieldSx = {
  minWidth: 0,
  '& .MuiInputBase-root': { minHeight: 44 }
} as const;

export function AdvancedSearch({ value, folders, onChange }: AdvancedSearchProps) {
  const [open, setOpen] = useState(false);
  const set = <K extends keyof SearchParams>(key: K, next: SearchParams[K]) =>
    onChange({ ...value, [key]: next });

  const active = [
    value.folder,
    value.pdf,
    value.from,
    value.to,
    value.mode,
    value.sort,
    value.variants === 'off' ? 'x' : ''
  ].filter(Boolean).length;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Divider />
      <Box
        sx={{
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap'
        }}
      >
        <Button
          color="inherit"
          startIcon={<TuneRoundedIcon />}
          endIcon={(
            <ExpandMoreRoundedIcon
              sx={{ transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          )}
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="advanced-search-panel"
          sx={{ px: 1, color: 'text.primary', minHeight: 40 }}
        >
          Suchoptionen
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          {active > 0 && (
            <>
              <Chip size="small" color="primary" label={`${active} aktiv`} />
              <Button
                size="small"
                color="inherit"
                startIcon={<RestartAltRoundedIcon />}
                onClick={() => onChange({})}
                sx={{ color: 'text.secondary', minHeight: 34, px: 1 }}
              >
                Zurücksetzen
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Collapse in={open} timeout={180}>
        <Box
          id="advanced-search-panel"
          sx={{
            pt: 0.5,
            pb: 1
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${layoutTokens.panelRadius}px`,
              backgroundColor: (theme) => theme.palette.action.hover
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Treffer eingrenzen</Typography>
            <Typography variant="caption" color="text.secondary">
              Die Auswahl wird direkt auf die aktuelle Suche angewendet.
            </Typography>

            <Box
              sx={{
                mt: 1.75,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))'
                },
                gap: 1.5
              }}
            >
              {folders.length > 1 && (
                <TextField
                  select
                  size="small"
                  label="Archivordner"
                  value={value.folder ?? ''}
                  onChange={(event) => set('folder', event.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value="">Alle Ordner</MenuItem>
                  {folders.map((folder) => (
                    <MenuItem key={folder.path} value={folder.path}>
                      {folder.path === '.' ? '/' : folder.path} ({folder.pdfs})
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                size="small"
                label="Wortabgleich"
                value={value.mode ?? ''}
                onChange={(event) => set('mode', event.target.value as SearchParams['mode'])}
                sx={fieldSx}
              >
                <MenuItem value="">Automatisch</MenuItem>
                <MenuItem value="word">Nur ganzes Wort</MenuItem>
                <MenuItem value="inside">Auch innerhalb von Wörtern</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Schreibweisen"
                value={value.variants ?? ''}
                onChange={(event) => set('variants', event.target.value as SearchParams['variants'])}
                sx={fieldSx}
              >
                <MenuItem value="">Deutsche Varianten berücksichtigen</MenuItem>
                <MenuItem value="off">Genau wie eingegeben</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Sortierung"
                value={value.sort ?? ''}
                onChange={(event) => set('sort', event.target.value as SearchParams['sort'])}
                sx={fieldSx}
              >
                <MenuItem value="">Nach Relevanz</MenuItem>
                <MenuItem value="page">Nach Seitenreihenfolge</MenuItem>
              </TextField>

              <TextField
                size="small"
                label="Seite von"
                type="number"
                value={value.from ?? ''}
                onChange={(event) => set('from', event.target.value)}
                inputProps={{ min: 1 }}
                sx={fieldSx}
              />
              <TextField
                size="small"
                label="Seite bis"
                type="number"
                value={value.to ?? ''}
                onChange={(event) => set('to', event.target.value)}
                inputProps={{ min: 1 }}
                sx={fieldSx}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${layoutTokens.panelRadius}px`,
              backgroundColor: 'background.paper'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Direkt im Suchfeld</Typography>
            <Typography variant="caption" color="text.secondary">
              Diese Operatoren lassen sich miteinander kombinieren.
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                mt: 1.5,
                p: 0,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(5, minmax(0, 1fr))'
                },
                gap: 1.5
              }}
            >
              {syntax.map((item) => (
                <Box component="li" key={item.example} sx={{ minWidth: 0 }}>
                  <Box
                    component="code"
                    sx={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12,
                      lineHeight: 1.5,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: (theme) => theme.palette.action.selected,
                      color: 'primary.main',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.example}
                  </Box>
                  <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                    {item.means}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
